import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, ArrowLeft, Activity, Users, Eye, CreditCard, TrendingUp, Download, Filter, RefreshCw, ChevronDown, BarChart3, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminAnalyticsLive,
  adminAnalyticsMetrics,
  adminAnalyticsTopPages,
  adminAnalyticsFunnel,
  adminAnalyticsEvents,
  adminAnalyticsEventTypes,
  adminAnalyticsExport,
} from "@/lib/api";
import { toast } from "sonner";

const RANGES = [
  { label: "Today", value: "today" },
  { label: "7D", value: "7d" },
  { label: "30D", value: "30d" },
];

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function LiveCard({ label, value, icon: Icon, color, pulse }) {
  return (
    <div className="relative rounded-xl border border-white/8 bg-white/[0.02] p-4 overflow-hidden" data-testid={`live-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {pulse && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[11px] uppercase tracking-wider text-white/30 font-[Manrope]">{label}</span>
      </div>
      <p className={`text-2xl font-light ${color} font-[Manrope]`}>{value}</p>
    </div>
  );
}

function MetricCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1 font-[Manrope]">{label}</p>
      <p className="text-xl font-light text-white/90 font-[Manrope]">{value}</p>
      {sub && <p className="text-xs text-white/30 mt-1 font-[Manrope]">{sub}</p>}
    </div>
  );
}

function FunnelBar({ step, count, maxCount }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="mb-3" data-testid={`funnel-step-${step.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-white/60 font-[Manrope]">{step}</span>
        <span className="text-xs text-white/40 font-[Manrope]">{count}</span>
      </div>
      <div className="h-6 rounded-lg bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-lg bg-gradient-to-r from-indigo-500/60 to-indigo-500/30 transition-all duration-500"
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </div>
  );
}

export default function AnalyticsDashboard({ passcode }) {
  const [live, setLive] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventTypes, setEventTypes] = useState([]);
  const [range, setRange] = useState("today");
  const [eventFilter, setEventFilter] = useState("");
  const [routeFilter, setRouteFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [eventsPage, setEventsPage] = useState(0);
  const [loadingLive, setLoadingLive] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const liveIntervalRef = useRef(null);

  // Load live data with auto-refresh
  const loadLive = useCallback(async () => {
    try {
      const data = await adminAnalyticsLive(passcode);
      setLive(data);
    } catch {}
    setLoadingLive(false);
  }, [passcode]);

  useEffect(() => {
    loadLive();
    liveIntervalRef.current = setInterval(loadLive, 15000);
    return () => clearInterval(liveIntervalRef.current);
  }, [loadLive]);

  // Load range-dependent data
  const loadRangeData = useCallback(async () => {
    setLoadingMetrics(true);
    try {
      const [m, tp, f] = await Promise.all([
        adminAnalyticsMetrics(passcode, range),
        adminAnalyticsTopPages(passcode, range),
        adminAnalyticsFunnel(passcode, range),
      ]);
      setMetrics(m);
      setTopPages(tp.pages || []);
      setFunnel(f.funnel || []);
    } catch {}
    setLoadingMetrics(false);
  }, [passcode, range]);

  useEffect(() => { loadRangeData(); }, [loadRangeData]);

  // Load event types once
  useEffect(() => {
    adminAnalyticsEventTypes(passcode).then((d) => setEventTypes(d.event_types || [])).catch(() => {});
  }, [passcode]);

  // Load events feed
  const loadEvents = useCallback(async () => {
    try {
      const params = { limit: "30", offset: String(eventsPage * 30) };
      if (eventFilter) params.event_type = eventFilter;
      if (routeFilter) params.route = routeFilter;
      if (deviceFilter) params.device = deviceFilter;
      const data = await adminAnalyticsEvents(passcode, params);
      setEvents(data.events || []);
      setEventsTotal(data.total || 0);
    } catch {}
  }, [passcode, eventsPage, eventFilter, routeFilter, deviceFilter]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Auto-refresh events feed
  useEffect(() => {
    const iv = setInterval(loadEvents, 15000);
    return () => clearInterval(iv);
  }, [loadEvents]);

  const handleExport = async (exportType) => {
    try {
      const blob = await adminAnalyticsExport(passcode, range, exportType);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analytics_${exportType}_${range}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportType} CSV`);
    } catch {
      toast.error("Export failed");
    }
  };

  const funnelMax = funnel.length > 0 ? Math.max(...funnel.map((f) => f.count), 1) : 1;

  return (
    <div data-testid="analytics-dashboard">
      {/* Section A: Live Overview */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-400" />
            <h2 className="text-base text-white/70 font-[Manrope]">Live Overview</h2>
            <span className="text-[10px] text-white/20 font-[Manrope]">Last 30 min</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-white/20">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Auto-refreshing
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <LiveCard label="Active Users" value={live?.active_users ?? "..."} icon={Users} color="text-emerald-400" pulse />
          <LiveCard label="Page Views" value={live?.page_views ?? "..."} icon={Eye} color="text-blue-400" />
          <LiveCard label="Sign-ins" value={live?.signins ?? "..."} icon={Activity} color="text-amber-400" />
          <LiveCard label="Revenue" value={live?.revenue != null ? `$${live.revenue}` : "..."} icon={CreditCard} color="text-pink-400" />
        </div>
      </div>

      {/* Range Selector + Export */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-[Manrope] transition-colors ${range === r.value ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"}`}
              data-testid={`range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="text-white/30 hover:text-white text-xs h-8" onClick={() => handleExport("events")} data-testid="export-events-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Events CSV
          </Button>
          <Button size="sm" variant="ghost" className="text-white/30 hover:text-white text-xs h-8" onClick={() => handleExport("purchases")} data-testid="export-purchases-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Purchases CSV
          </Button>
        </div>
      </div>

      {/* Section C: Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="key-metrics-grid">
        <MetricCard label="Unique Visitors" value={metrics?.unique_visitors ?? "..."} />
        <MetricCard label="Page Views" value={metrics?.total_page_views ?? "..."} />
        <MetricCard label="Avg Session" value={metrics?.avg_session_seconds != null ? formatDuration(metrics.avg_session_seconds) : "..."} />
        <MetricCard label="Conversion" value={metrics?.conversion_rate != null ? `${metrics.conversion_rate}%` : "..."} sub={`${metrics?.total_purchases ?? 0} purchases`} />
        <MetricCard label="Revenue" value={metrics?.total_revenue != null ? `$${metrics.total_revenue}` : "..."} sub={`${metrics?.checkouts_started ?? 0} checkouts`} />
      </div>

      {/* Section D + E: Top Pages + Funnel side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Pages */}
        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-5" data-testid="top-pages-section">
          <h3 className="text-xs uppercase tracking-wider text-white/30 mb-4 font-[Manrope] flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" /> Top Pages
          </h3>
          {topPages.length === 0 ? (
            <p className="text-sm text-white/20 font-[Manrope]">No page view data yet</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-white/20 pb-2 border-b border-white/5 font-[Manrope]">
                <span>Route</span><span className="text-right">Views</span><span className="text-right">Visitors</span>
              </div>
              {topPages.map((p) => (
                <div key={p.route} className="grid grid-cols-3 gap-2 text-sm py-1.5" data-testid={`top-page-${p.route}`}>
                  <span className="text-white/70 truncate text-xs font-[Manrope]">{p.route}</span>
                  <span className="text-right text-white/50 text-xs font-[Manrope]">{p.views}</span>
                  <span className="text-right text-white/40 text-xs font-[Manrope]">{p.unique_visitors}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Funnel */}
        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-5" data-testid="funnel-section">
          <h3 className="text-xs uppercase tracking-wider text-white/30 mb-4 font-[Manrope] flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Conversion Funnel
          </h3>
          {funnel.length === 0 ? (
            <p className="text-sm text-white/20 font-[Manrope]">No data yet</p>
          ) : (
            funnel.map((f) => <FunnelBar key={f.step} step={f.step} count={f.count} maxCount={funnelMax} />)
          )}
        </div>
      </div>

      {/* Section F: Live Event Stream */}
      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-5 mb-8" data-testid="event-stream-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xs uppercase tracking-wider text-white/30 font-[Manrope] flex items-center gap-2">
            <Activity className="w-3.5 h-3.5" /> Event Stream
            <span className="text-white/15">({eventsTotal} total)</span>
          </h3>
          <Button size="sm" variant="ghost" className="text-white/20 hover:text-white h-6 text-[10px]" onClick={() => { setEventsPage(0); loadEvents(); }}>
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="event-filters">
          <select
            value={eventFilter}
            onChange={(e) => { setEventFilter(e.target.value); setEventsPage(0); }}
            className="h-7 rounded-md border border-white/10 bg-transparent text-white/60 text-[11px] px-2 font-[Manrope]"
            data-testid="filter-event-type"
          >
            <option value="">All Events</option>
            {eventTypes.map((t) => <option key={t.name || t} value={t.name || t}>{(t.name || t)}{t.count != null ? ` (${t.count})` : ""}</option>)}
          </select>
          <Input
            placeholder="Filter by route..."
            value={routeFilter}
            onChange={(e) => { setRouteFilter(e.target.value); setEventsPage(0); }}
            className="h-7 w-32 bg-transparent border-white/10 text-white/60 text-[11px] font-[Manrope] placeholder:text-white/20"
            data-testid="filter-route"
          />
          <select
            value={deviceFilter}
            onChange={(e) => { setDeviceFilter(e.target.value); setEventsPage(0); }}
            className="h-7 rounded-md border border-white/10 bg-transparent text-white/60 text-[11px] px-2 font-[Manrope]"
            data-testid="filter-device"
          >
            <option value="">All Devices</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
          </select>
        </div>

        {/* Events table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-white/20 border-b border-white/5">
                <th className="text-left py-2 font-normal font-[Manrope]">Time</th>
                <th className="text-left py-2 font-normal font-[Manrope]">Event</th>
                <th className="text-left py-2 font-normal font-[Manrope]">Route</th>
                <th className="text-left py-2 font-normal font-[Manrope]">Session</th>
                <th className="text-left py-2 font-normal font-[Manrope]">Device</th>
                <th className="text-right py-2 font-normal font-[Manrope]">Value</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-white/20 font-[Manrope]">No events recorded yet. Activity will appear here in real-time.</td></tr>
              ) : events.map((evt, i) => (
                <tr key={`${evt.id || i}`} className="border-b border-white/3 hover:bg-white/[0.02]">
                  <td className="py-2 text-white/30 font-[Manrope] whitespace-nowrap">
                    {evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : "—"}
                  </td>
                  <td className="py-2 font-[Manrope]">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      evt.event_name === "purchase_success" ? "bg-emerald-500/15 text-emerald-400" :
                      evt.event_name === "checkout_started" ? "bg-amber-500/15 text-amber-400" :
                      evt.event_name?.includes("login") || evt.event_name?.includes("signup") ? "bg-blue-500/15 text-blue-400" :
                      evt.event_name === "page_view" ? "bg-white/5 text-white/40" :
                      "bg-white/5 text-white/50"
                    }`}>
                      {evt.event_name}
                    </span>
                  </td>
                  <td className="py-2 text-white/40 font-[Manrope] max-w-[120px] truncate">{evt.route || "—"}</td>
                  <td className="py-2 text-white/20 font-mono text-[10px]">{evt.session_id || "—"}</td>
                  <td className="py-2 text-white/30 font-[Manrope]">{evt.device_type || "—"}</td>
                  <td className="py-2 text-right text-white/50 font-[Manrope]">
                    {evt.value != null ? `$${evt.value}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {eventsTotal > 30 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
            <span className="text-[10px] text-white/20 font-[Manrope]">
              Showing {eventsPage * 30 + 1}-{Math.min((eventsPage + 1) * 30, eventsTotal)} of {eventsTotal}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-white/30 h-6 text-[10px]" disabled={eventsPage === 0} onClick={() => setEventsPage((p) => p - 1)}>
                Prev
              </Button>
              <Button size="sm" variant="ghost" className="text-white/30 h-6 text-[10px]" disabled={(eventsPage + 1) * 30 >= eventsTotal} onClick={() => setEventsPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
